import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Percent, TrendingUp, Wallet, Clock, Settings2, Plus, X, Save, Layers, Pencil, Trash2, Coins } from "lucide-react";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

const BASE_LABELS = { profit_no_iva: "Ganancia bruta (sin IVA)", profit: "Ganancia bruta", collected: "Venta completa (facturado)", profit_less_deduction: "Ganancia − % impuestos y otros" };
const PLAN_MODES = [
  { v: "collected", l: "Venta completa (monto facturado)" },
  { v: "profit", l: "Ganancia bruta" },
  { v: "profit_less_deduction", l: "Ganancia − % impuestos y otros" },
];
const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const emptyPlan = { name: "", base_mode: "profit", deduction_pct: 0, include_base_salary: false, tiers: [{ up_to: null, pct: 3 }] };

function Stat({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card className={`rounded-sm border shadow-none p-4 ${tone}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest opacity-70"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <p className="text-2xl font-heading font-bold mt-1">{value}</p>
    </Card>
  );
}

export default function Commissions() {
  const [month, setMonth] = useState(nowMonth());
  const [vendorId, setVendorId] = useState("");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  // Planes
  const [plansOpen, setPlansOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planForm, setPlanForm] = useState(null); // null = lista, obj = editar/crear

  const load = useCallback(() => {
    setLoading(true);
    const vq = vendorId ? `&vendor_id=${vendorId}` : "";
    Promise.all([api.get(`/commissions?month=${month}${vq}`), api.get(`/commissions/history?months=6${vendorId ? `&vendor_id=${vendorId}` : ""}`)])
      .then(([c, h]) => {
        setData(c.data); setHistory(h.data.history || []);
        if (c.data.can_manage) { setPlans(c.data.plans || []); if (vendors.length === 0) api.get("/commissions/vendors").then((r) => setVendors(r.data)).catch(() => {}); }
      })
      .catch(() => {}).finally(() => setLoading(false));
  }, [month, vendorId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const canManage = data?.can_manage;
  const assignedPlanId = data?.assigned_plan_id || "";

  const openConfig = () => { setCfg(JSON.parse(JSON.stringify(data.config))); setCfgOpen(true); };
  const addTier = () => setCfg((c) => ({ ...c, tiers: [...c.tiers, { up_to: 0, pct: 0 }] }));
  const updTier = (i, k, v) => setCfg((c) => ({ ...c, tiers: c.tiers.map((t, idx) => idx === i ? { ...t, [k]: v === "" ? null : Number(v) } : t) }));
  const rmTier = (i) => setCfg((c) => ({ ...c, tiers: c.tiers.filter((_, idx) => idx !== i) }));
  const saveConfig = async () => {
    try { await api.put("/commissions/config", { base_mode: cfg.base_mode, iva_pct: Number(cfg.iva_pct), tiers: cfg.tiers }); toast.success("Acuerdo global actualizado"); setCfgOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Planes ----
  const refreshPlans = () => api.get("/commissions/plans").then((r) => setPlans(r.data)).catch(() => {});
  const openPlans = () => { setPlanForm(null); setPlansOpen(true); refreshPlans(); };
  const newPlan = () => setPlanForm(JSON.parse(JSON.stringify(emptyPlan)));
  const editPlan = (p) => setPlanForm(JSON.parse(JSON.stringify({ ...emptyPlan, ...p, tiers: p.tiers?.length ? p.tiers : [{ up_to: null, pct: 0 }] })));
  const pAddTier = () => setPlanForm((f) => ({ ...f, tiers: [...f.tiers, { up_to: 0, pct: 0 }] }));
  const pUpdTier = (i, k, v) => setPlanForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v === "" ? null : Number(v) } : t) }));
  const pRmTier = (i) => setPlanForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  const savePlan = async () => {
    const body = { name: planForm.name, base_mode: planForm.base_mode, deduction_pct: Number(planForm.deduction_pct || 0), include_base_salary: !!planForm.include_base_salary, tiers: planForm.tiers };
    try {
      if (planForm.id) await api.put(`/commissions/plans/${planForm.id}`, body);
      else await api.post("/commissions/plans", body);
      toast.success("Plan guardado"); setPlanForm(null); refreshPlans(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deletePlan = async (p) => {
    try { await api.delete(`/commissions/plans/${p.id}`); toast.success("Plan eliminado"); refreshPlans(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const assignPlan = async (planId) => {
    try { await api.post("/commissions/assign", { vendor_id: vendorId, plan_id: planId === "none" ? "" : planId }); toast.success("Plan asignado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Comisiones</h1>
          <p className="text-sm text-muted-foreground">Sueldo base fijo + comisión ganada al cobrar. La comisión se calcula según el plan del vendedor.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">Mes</Label>
            <Input type="month" className="rounded-sm h-9" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="commission-month" /></div>
          {canManage && vendors.length > 0 && (
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">Vendedor</Label>
              <Select value={vendorId || "me"} onValueChange={(v) => setVendorId(v === "me" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 w-52" data-testid="commission-vendor"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="me">Yo ({s.vendor_name || "—"})</SelectItem>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          {canManage && vendorId && (
            <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">Plan del vendedor</Label>
              <Select value={assignedPlanId || "none"} onValueChange={assignPlan}>
                <SelectTrigger className="rounded-sm h-9 w-56" data-testid="commission-assign-plan"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Acuerdo global</SelectItem>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          {canManage && <Button variant="outline" className="rounded-sm gap-2 h-9" onClick={openPlans} data-testid="commission-plans-btn"><Layers className="h-4 w-4" /> Planes</Button>}
          {canManage && data && <Button variant="outline" className="rounded-sm gap-2 h-9" onClick={openConfig} data-testid="commission-config-btn"><Settings2 className="h-4 w-4" /> Acuerdo global</Button>}
        </div>
      </div>

      {loading || !data ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <>
          <div className="text-xs text-muted-foreground">Plan: <span className="font-semibold">{s.plan_name}</span> · Base: <span className="font-semibold">{BASE_LABELS[s.base_mode] || s.base_mode}</span> · Tarifa por meta: <Badge className="rounded-sm bg-primary/15 text-primary">{s.applied_pct}%</Badge> · Vendedor: <span className="font-semibold">{s.vendor_name}</span></div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Stat icon={Coins} label="Total a pagar" value={money("GTQ", s.total_payout)} tone="border-primary/30 bg-primary/10" testid="stat-total" />
            <Stat icon={Wallet} label="Sueldo base" value={money("GTQ", s.base_salary)} tone="" testid="stat-salary" />
            <Stat icon={TrendingUp} label="Comisión cobrable" value={money("GTQ", s.commission_earned)} tone="border-green-500/20 bg-green-500/10" testid="stat-earned" />
            <Stat icon={Clock} label="Comisión en proceso" value={money("GTQ", s.commission_pending)} tone="border-amber-500/20 bg-amber-500/10" testid="stat-pending" />
            <Stat icon={Percent} label="% aplicado" value={`${s.applied_pct}%`} tone="border-primary/20 bg-primary/5" testid="stat-pct" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="rounded-sm border shadow-none overflow-hidden lg:col-span-2">
              <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-bold">Detalle por factura ({s.invoice_count})</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="commission-invoices">
                  <thead><tr className="bg-muted text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2.5 font-bold">Factura</th><th className="px-3 py-2.5 font-bold">Cliente</th>
                    <th className="px-3 py-2.5 font-bold text-right">Total</th><th className="px-3 py-2.5 font-bold text-right">Cobrado</th>
                    <th className="px-3 py-2.5 font-bold text-right">Base</th><th className="px-3 py-2.5 font-bold text-right">Base cobrada</th>
                    <th className="px-3 py-2.5 font-bold">Estado</th></tr></thead>
                  <tbody>
                    {data.invoices.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Sin facturas este mes.</td></tr>}
                    {data.invoices.map((r) => (
                      <tr key={r.invoice_id} className="border-t border-border" data-testid={`comm-inv-${r.number}`}>
                        <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                        <td className="px-3 py-2">{r.client_name}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{money(r.currency, r.total)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{money(r.currency, r.paid)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{money(r.currency, r.base)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold">{money(r.currency, r.collected_base)}</td>
                        <td className="px-3 py-2"><Badge className={`rounded-sm text-[10px] ${r.status === "Pagada" ? "bg-green-100 text-green-700" : r.balance > 0 ? "bg-amber-100 text-amber-700" : ""}`}>{r.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="rounded-sm border shadow-none p-4">
              <h3 className="font-heading font-bold mb-1">Historial (6 meses)</h3>
              <p className="text-xs text-muted-foreground mb-3">Total a pagar por mes (base + comisión).</p>
              <div className="space-y-2" data-testid="commission-history">
                {history.map((h) => (
                  <div key={h.month} className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                    <div><p className="text-sm font-semibold">{h.month}</p><p className="text-[10px] text-muted-foreground">{h.applied_pct}% · comisión {money("GTQ", h.commission_earned)}</p></div>
                    <div className="text-right"><p className="text-sm font-mono font-bold text-primary">{money("GTQ", h.total_payout)}</p>{h.commission_pending > 0 && <p className="text-[10px] text-amber-600">+{money("GTQ", h.commission_pending)} en proceso</p>}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Planes de comisión */}
      <Dialog open={plansOpen} onOpenChange={(o) => { setPlansOpen(o); if (!o) setPlanForm(null); }}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plans-dialog">
          <DialogHeader><DialogTitle className="font-heading">Planes de comisión</DialogTitle>
            <DialogDescription>Crea planes reutilizables y asígnalos a cada vendedor desde el selector "Plan del vendedor".</DialogDescription></DialogHeader>

          {!planForm ? (
            <div className="space-y-3">
              <Button size="sm" className="rounded-sm gap-1 w-fit" onClick={newPlan} data-testid="new-plan-btn"><Plus className="h-4 w-4" /> Nuevo plan</Button>
              <div className="space-y-2">
                {plans.length === 0 && <p className="text-sm text-muted-foreground">Sin planes. Crea el primero.</p>}
                {plans.map((p) => (
                  <div key={p.id} className="border border-border rounded-sm p-3 flex items-start justify-between gap-3" data-testid={`plan-item-${p.id}`}>
                    <div>
                      <div className="font-semibold text-sm flex items-center gap-2">{p.name} {p.include_base_salary && <Badge className="rounded-sm text-[10px] bg-blue-500/15 text-blue-600">+ sueldo base</Badge>}</div>
                      <p className="text-xs text-muted-foreground">{BASE_LABELS[p.base_mode] || p.base_mode}{p.base_mode === "profit_less_deduction" ? ` (−${p.deduction_pct}%)` : ""} · {p.tiers?.length || 0} meta(s)</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => editPlan(p)} data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => deletePlan(p)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Nombre del plan</Label>
                <Input className="rounded-sm" placeholder="Ej. Base + comisión sobre ganancia" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} data-testid="plan-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Base del cálculo</Label>
                  <Select value={planForm.base_mode} onValueChange={(v) => setPlanForm({ ...planForm, base_mode: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="plan-base"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLAN_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent></Select></div>
                {planForm.base_mode === "profit_less_deduction" && (
                  <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">% impuestos y otros</Label>
                    <Input type="number" className="rounded-sm" value={planForm.deduction_pct} onChange={(e) => setPlanForm({ ...planForm, deduction_pct: e.target.value })} data-testid="plan-deduction" /></div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="h-4 w-4 rounded-sm" checked={!!planForm.include_base_salary} onChange={(e) => setPlanForm({ ...planForm, include_base_salary: e.target.checked })} data-testid="plan-include-salary" />
                Incluir sueldo base (se toma de la ficha del colaborador y se suma a la comisión)
              </label>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">Metas escalonadas (% según venta)</Label>
                  <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1 h-7 text-xs" onClick={pAddTier} data-testid="plan-add-tier"><Plus className="h-3.5 w-3.5" /> Meta</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">Deja "Hasta" vacío en la última meta para "en adelante".</p>
                <div className="space-y-2">
                  {planForm.tiers.map((t, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`plan-tier-${i}`}>
                      <span className="text-xs text-muted-foreground w-10">Hasta</span>
                      <Input type="number" className="h-8 rounded-sm text-xs" placeholder="∞" value={t.up_to === null ? "" : t.up_to} onChange={(e) => pUpdTier(i, "up_to", e.target.value)} />
                      <Input type="number" className="h-8 rounded-sm text-xs w-20" value={t.pct} onChange={(e) => pUpdTier(i, "pct", e.target.value)} />
                      <span className="text-xs">%</span>
                      <button type="button" onClick={() => pRmTier(i)} className="text-destructive p-1"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setPlanForm(null)}>Volver</Button>
                <Button className="rounded-sm font-bold gap-1" onClick={savePlan} disabled={!planForm.name} data-testid="save-plan-btn"><Save className="h-4 w-4" /> Guardar plan</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Acuerdo global (respaldo) */}
      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="commission-config-dialog">
          <DialogHeader><DialogTitle className="font-heading">Acuerdo global (respaldo)</DialogTitle>
            <DialogDescription>Se aplica a los vendedores que NO tienen un plan asignado.</DialogDescription></DialogHeader>
          {cfg && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Base del cálculo</Label>
                  <Select value={cfg.base_mode} onValueChange={(v) => setCfg({ ...cfg, base_mode: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="cfg-base"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="profit_no_iva">Ganancia bruta (sin IVA)</SelectItem><SelectItem value="profit">Ganancia bruta</SelectItem><SelectItem value="collected">Monto facturado</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">IVA % (para descontar)</Label>
                  <Input type="number" className="rounded-sm" value={cfg.iva_pct} onChange={(e) => setCfg({ ...cfg, iva_pct: e.target.value })} data-testid="cfg-iva" /></div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold uppercase tracking-widest">Metas escalonadas</Label>
                  <Button type="button" size="sm" variant="outline" className="rounded-sm gap-1 h-7 text-xs" onClick={addTier} data-testid="cfg-add-tier"><Plus className="h-3.5 w-3.5" /> Meta</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">Deja "Hasta" vacío en la última meta para "en adelante".</p>
                <div className="space-y-2">
                  {cfg.tiers.map((t, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`cfg-tier-${i}`}>
                      <span className="text-xs text-muted-foreground w-10">Hasta</span>
                      <Input type="number" className="h-8 rounded-sm text-xs" placeholder="∞" value={t.up_to === null ? "" : t.up_to} onChange={(e) => updTier(i, "up_to", e.target.value)} />
                      <Input type="number" className="h-8 rounded-sm text-xs w-20" value={t.pct} onChange={(e) => updTier(i, "pct", e.target.value)} />
                      <span className="text-xs">%</span>
                      <button type="button" onClick={() => rmTier(i)} className="text-destructive p-1"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCfgOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold gap-1" onClick={saveConfig} data-testid="save-config-btn"><Save className="h-4 w-4" /> Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
